import React, { useMemo, useState } from 'react';
import { Customer } from '../types';
import { usePos } from '../context/PosContext';
import './Products.css';
import './Customers.css';

const blank = {
  name: '',
  phone: '',
  altNo: '',
  vehicleNo: '',
  oldBalance: '',
  advance: '',
  creditLimit: '',
  dayLimit: '',
  mealDays: '',
  allowCredit: false,
  extraDetail: '',
  village: '',
  address: '',
  type: 'CUSTOMER' as 'CUSTOMER' | 'INHOUSE',
  aadhaar: '',
  pan: '',
  gstin: '',
  email: '',
  refName: '',
};

const Customers: React.FC = () => {
  const { customers, addCustomer, updateCustomer, deleteCustomer, formatMoney, isOn } = usePos();
  const showVehicle = isOn('enable_vehicle_number'); // "D - Add Vehicle Number"
  const showAdvance = isOn('enable_customer_advance'); // "B - Customer Advance"
  const [editing, setEditing] = useState<Customer | null>(null);
  const [listSearch, setListSearch] = useState('');
  const [f, setF] = useState({ ...blank });

  const set = (k: keyof typeof blank, v: string | boolean) => setF((prev) => ({ ...prev, [k]: v }));

  const filtered = useMemo(() => {
    const t = listSearch.trim().toLowerCase();
    if (!t) return customers;
    return customers.filter((c) => c.name.toLowerCase().includes(t) || c.phone?.includes(listSearch));
  }, [customers, listSearch]);

  const reset = () => {
    setF({ ...blank });
    setEditing(null);
  };

  const num = (s: string) => (s ? parseFloat(s) : undefined);

  const buildData = () => ({
    name: f.name,
    phone: f.phone,
    altNo: f.altNo || undefined,
    vehicleNo: f.vehicleNo || undefined,
    oldBalance: num(f.oldBalance),
    advance: num(f.advance),
    creditLimit: num(f.creditLimit),
    dayLimit: num(f.dayLimit),
    mealDays: num(f.mealDays),
    allowCredit: f.allowCredit,
    extraDetail: f.extraDetail || undefined,
    village: f.village || undefined,
    address: f.address,
    type: f.type,
    aadhaar: f.aadhaar || undefined,
    pan: f.pan || undefined,
    gstin: f.gstin || undefined,
    email: f.email || undefined,
    refName: f.refName || undefined,
    creditBalance: num(f.oldBalance) ?? 0,
  });

  const save = () => {
    if (!f.name || !f.phone) {
      alert('Name and Mobile are required.');
      return;
    }
    if (editing) updateCustomer({ ...editing, ...buildData() });
    else addCustomer(buildData());
    reset();
  };

  const edit = (c: Customer) => {
    setEditing(c);
    setF({
      name: c.name,
      phone: c.phone ?? '',
      altNo: c.altNo ?? '',
      vehicleNo: c.vehicleNo ?? '',
      oldBalance: c.oldBalance != null ? String(c.oldBalance) : c.creditBalance != null ? String(c.creditBalance) : '',
      advance: c.advance != null ? String(c.advance) : '',
      creditLimit: c.creditLimit != null ? String(c.creditLimit) : '',
      dayLimit: c.dayLimit != null ? String(c.dayLimit) : '',
      mealDays: c.mealDays != null ? String(c.mealDays) : '',
      allowCredit: c.allowCredit ?? false,
      extraDetail: c.extraDetail ?? '',
      village: c.village ?? '',
      address: c.address ?? '',
      type: c.type ?? 'CUSTOMER',
      aadhaar: c.aadhaar ?? '',
      pan: c.pan ?? '',
      gstin: c.gstin ?? '',
      email: c.email ?? '',
      refName: c.refName ?? '',
    });
  };

  const remove = () => {
    if (editing && window.confirm('Delete this customer?')) {
      deleteCustomer(editing.id);
      reset();
    }
  };

  const lbl = (t: string) => <label className="pf-lbl">{t}</label>;

  return (
    <div className="pm-page">
      {/* list */}
      <div className="pm-list card-rounded">
        <input className="pm-list-search" placeholder="Search Customer" value={listSearch} onChange={(e) => setListSearch(e.target.value)} />
        <div className="pm-list-scroll">
          {filtered.map((c) => (
            <button key={c.id} className={`pm-list-item ${editing?.id === c.id ? 'sel' : ''}`} onClick={() => edit(c)}>
              <span className="pm-li-name">{c.name}</span>
              <span className="pm-li-meta">
                {c.phone || '—'} · Bal {formatMoney(c.creditBalance || 0)}
              </span>
            </button>
          ))}
          {filtered.length === 0 && <p className="muted" style={{ padding: 16 }}>No customers.</p>}
        </div>
      </div>

      {/* form matching add_customer_details_layout.xml */}
      <div className="pm-form card-rounded">
        <div className="pf-top">
          <input className="pf-top-search" placeholder="Search Customer" value={listSearch} onChange={(e) => setListSearch(e.target.value)} />
          <div className="pf-top-btns">
            {editing && (
              <button className="pf-btn-orange" onClick={remove}>
                Delete
              </button>
            )}
            <button className="pf-btn-orange" onClick={save}>
              {editing ? 'Update' : 'Save'}
            </button>
          </div>
        </div>

        <div className="pf-scroll">
          <div className="pf-row r2">
            <div>{lbl('Name *')}<input value={f.name} onChange={(e) => set('name', e.target.value)} /></div>
            <div>{lbl('Mobile *')}<input value={f.phone} onChange={(e) => set('phone', e.target.value)} /></div>
          </div>
          <div className="pf-row r2">
            <div>{lbl('Alternate No')}<input value={f.altNo} onChange={(e) => set('altNo', e.target.value)} /></div>
            {showVehicle && (
              <div>{lbl('Vehicle no')}<input value={f.vehicleNo} onChange={(e) => set('vehicleNo', e.target.value)} /></div>
            )}
          </div>
          <div className="pf-row r2">
            <div>{lbl('Old Balance')}<input type="number" value={f.oldBalance} onChange={(e) => set('oldBalance', e.target.value)} /></div>
            {showAdvance && (
              <div>{lbl('Advance Amount')}<input type="number" value={f.advance} onChange={(e) => set('advance', e.target.value)} /></div>
            )}
          </div>
          <div className="pf-row r3">
            <div>{lbl('Credit limit *')}<input type="number" value={f.creditLimit} onChange={(e) => set('creditLimit', e.target.value)} /></div>
            <div>{lbl('Day Limit *')}<input type="number" value={f.dayLimit} onChange={(e) => set('dayLimit', e.target.value)} /></div>
            <div>{lbl('Meal Days')}<input type="number" value={f.mealDays} onChange={(e) => set('mealDays', e.target.value)} /></div>
          </div>
          <div className="pf-checks">
            <label>
              <input type="checkbox" checked={f.allowCredit} onChange={(e) => set('allowCredit', e.target.checked)} /> Credit
            </label>
            <div className="pf-typeseg">
              <span className="pf-lbl" style={{ marginRight: 8 }}>Type</span>
              <button className={f.type === 'CUSTOMER' ? 'on' : ''} onClick={() => set('type', 'CUSTOMER')}>
                CUSTOMER
              </button>
              <button className={f.type === 'INHOUSE' ? 'on' : ''} onClick={() => set('type', 'INHOUSE')}>
                INHOUSE
              </button>
            </div>
          </div>

          <div className="pf-section">Extra Detail</div>
          <div className="pf-row r3">
            <div>{lbl('Extra Detail')}<input value={f.extraDetail} onChange={(e) => set('extraDetail', e.target.value)} /></div>
            <div>{lbl('Village name')}<input value={f.village} onChange={(e) => set('village', e.target.value)} /></div>
            <div>{lbl('Ref. Name')}<input value={f.refName} onChange={(e) => set('refName', e.target.value)} /></div>
          </div>
          <div className="pf-row r1">
            <div>{lbl('Address')}<textarea value={f.address} onChange={(e) => set('address', e.target.value)} /></div>
          </div>
          <div className="pf-row r3">
            <div>{lbl('Adhar Card No')}<input value={f.aadhaar} onChange={(e) => set('aadhaar', e.target.value)} /></div>
            <div>{lbl('Pan Card Number')}<input value={f.pan} onChange={(e) => set('pan', e.target.value)} /></div>
            <div>{lbl('GST No')}<input value={f.gstin} onChange={(e) => set('gstin', e.target.value)} /></div>
          </div>
          <div className="pf-row r2">
            <div>{lbl('Email Id')}<input value={f.email} onChange={(e) => set('email', e.target.value)} /></div>
            <div />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Customers;
